import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
}

export function RichTextEditor({ value, onChange }: RichTextEditorProps) {
  const [showHtml, setShowHtml] = useState(false);
  const [rawHtml, setRawHtml] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3, 4],
        },
      }),
      Placeholder.configure({
        placeholder: 'Compose the body content. Use the toolbar or keyboard shortcuts for formatting.',
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          'min-h-[240px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base leading-relaxed text-slate-800 shadow-soft focus:outline-none',
      },
    },
    autofocus: false,
    onUpdate({ editor: instance }) {
      const html = instance.getHTML();
      setRawHtml(html);
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (!showHtml && value !== current) {
      editor.commands.setContent(value, { emitUpdate: false });
      setRawHtml(value);
    }
  }, [value, editor, showHtml]);

  const toggleHtmlView = useCallback(() => {
    if (!editor) return;
    if (showHtml) {
      editor.commands.setContent(rawHtml, { emitUpdate: false });
    } else {
      setRawHtml(editor.getHTML());
    }
    setShowHtml((prev) => !prev);
  }, [editor, showHtml, rawHtml]);

  const handleHtmlChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const html = event.target.value;
      setRawHtml(html);
      onChange(html);
    },
    [onChange],
  );

  if (!editor) {
    return <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('bold') ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            Bold
          </button>
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('italic') ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            Italic
          </button>
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('heading', { level: 2 }) ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </button>
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('heading', { level: 3 }) ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </button>
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('bulletList') ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            Bullets
          </button>
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('orderedList') ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            Numbered
          </button>
          <button
            type="button"
            className={`secondary-button h-9 px-3 py-1 text-xs ${editor.isActive('blockquote') ? 'border-brand-300 text-brand-600' : ''}`}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            Quote
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="secondary-button h-9 px-3 py-1 text-xs" onClick={() => editor.chain().focus().undo().run()}>
            Undo
          </button>
          <button type="button" className="secondary-button h-9 px-3 py-1 text-xs" onClick={() => editor.chain().focus().redo().run()}>
            Redo
          </button>
          <button type="button" className="primary-button h-9 px-3 py-1 text-xs" onClick={toggleHtmlView}>
            {showHtml ? 'Back to editor' : 'HTML view'}
          </button>
        </div>
      </div>

      {showHtml ? (
        <textarea
          className="input-textarea min-h-[240px] font-mono text-sm"
          value={rawHtml}
          onChange={handleHtmlChange}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
}
